<template>
    <v-form ref="editForm" v-model="valid" @submit="save(false)" @submit.prevent>
        <v-card>
            <v-card-title v-if="!hideTitle">
                <span :class="{ title: isHover, headline: !isHover }">{{ mode }}{{ formTitle }}</span>
            </v-card-title>

            <v-card-text>
                <template v-for="field in fields">
                    <template v-if="field.type === 'checkbox'">
                        <v-checkbox :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :dense="isHover" :class="{ 'checkbox-dense': isHover }" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'date'">
                        <v-text-field type="date" :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :dense="isHover" :disabled="disabled" :has-default-date="!isEdit && field.rules.length != 0" />
                    </template>
                    <template v-else-if="field.type === 'datetime'">
                        <date-time-field :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :hide-offset="hideOffset" :dense="isHover" :disabled="disabled" :has-default-date="!isEdit && field.rules.length != 0" />
                    </template>
                    <template v-else-if="field.type === 'number'">
                        <v-text-field type="number" :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'time'">
                        <v-text-field type="time" :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'text'">
                        <v-text-field :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :counter="field.maxLength" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'percentage'">
                        <percentage :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                        <v-textarea :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :readonly="readonly || isHover" :required="field.isRequired" :counter="field.maxLength" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'autocomplete' && !isHover">
                        <autocomplete :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" item-text="label" item-value="id" :url="'/api/' + field.apiMethod + '/Autocomplete'" :addForm="field.form" :filter="field.ignoreFilter ? null : filter" :readonly="readonly" :disabled="disabled" :claim="field.claim" />
                    </template>
                    <template v-else-if="field.type === 'autocomplete' && isHover">
                        <hover-autocomplete :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :api-method="field.apiMethod" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'enum'">
                        <v-select :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :items="field.items" :label="field.text" :rules="field.rules" :required="field.isRequired" :multiple="field.isMultiple" :dense="isHover" :readonly="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'coordinates'">
                        <coordinate-picker :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :items="field.items" :label="field.text" :rules="field.rules" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'contact'">
                        <contact-fields :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :contact-id="item[field.contactField]" :label="field.text" :rules="field.rules" :dense="isHover" :readonly="isHover" :disabled="disabled" :is-edit="isEdit" :no-edit-email-address="field.noEditEmailAddress" :remove-email-address="field.removeEmailAddress" />
                    </template>
                    <template v-else-if="field.type === 'address'">
                        <address-field :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :rules="field.rules" :dense="isHover" :readonly="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'file'">
                        <file-upload :key="field.value" v-if="!isFiltered(field.value)" v-model="item[field.value]" :label="field.text" :readonly="readonly || isHover" :dense="isHover" :disabled="disabled" />
                    </template>
                    <template v-else-if="field.type === 'files'">
                        <files-field :key="field.value" v-if="!isFiltered(field.value) && !isHover" v-model="item[field.value]" :form="field.form" :item-id="item.id" :parent-column-name="field.parentColumnName" :label="field.text" :readonly="readonly || isHover" :disabled="disabled || !canSave" @save="save(true)" :can-open="!isSaving" />
                    </template>
                    <template v-else-if="field.type === 'custom'">
                        <slot name="field" :item="item" :setter="setItem" :adjuster="adjustItem" :dense="isHover" :disabled="disabled" />
                    </template>
                </template>
            </v-card-text>

            <v-card-actions v-if="!isHover">
                <v-btn v-if="isEdit && !hideDelete" color="quinary" text @click="deleteItem" :disabled="readonly">Delete</v-btn>
                <div class="flex-grow-1"></div>
                <v-btn v-if="!hideCancel" color="quaternary" text @click="cancel">Cancel</v-btn>
                <v-btn type="submit" color="quaternary" text :disabled="readonly || isSaving">Save</v-btn>
            </v-card-actions>
            <v-progress-linear v-if="isSaving" indeterminate color="blue" />
        </v-card>
    </v-form>
</template>

<script>
    import axios from 'axios'
    import errorUtility from '../services/errorUtility'

    export default {
        props: [
            'value',
            'filter',
            'hideDelete',
            'readonly',
            'hideOffset',
            'apiMethod',
            'fields',
            'formTitle',
            'initialiseItem',
            'isHover',
            'disabled',
            'hideTitle'
        ],

        components: {
            autocomplete: () => import('../components/Autocomplete'),
            hoverAutocomplete: () => import('../components/HoverAutocomplete'),
            dateTimeField: () => import('../components/DateTimeField'),
            coordinatePicker: () => import('../components/CoordinatePicker'),
            contactFields: () => import('../components/ContactFields'),
            fileUpload: () => import('../components/FileUpload'),
            filesField: () => import('../components/FilesField'),
            addressField: () => import('../components/AddressField'),
            percentage: () => import('../components/Percentage')
        },

        data() {
            return {
                valid: true,
                isSaving: false,
                item: {},
                hideCancel: false
            }
        },

        async mounted() {
            if (this.isHover) {
                var response = await axios.get('/api/' + this.apiMethod + '/' + this.value);
                this.item = response.data;
            }
        },

        methods: {
            getUrl() {
                return '/api/' + this.apiMethod + '/Autocomplete';
            },

            async deleteItem() {
                if (!this.isEdit || !confirm('Are you sure you want to delete this item?')) {
                    return;
                }

                try {
                    await axios.delete('/api/' + this.apiMethod + '/' + this.item.id);
                    this.$emit('delete', this.item);
                } catch (error) {
                    errorUtility.handleServerError(error);
                }
            },

            async save(persistForm) {
                this.hideCancel = false;
                if (this.isSaving) { return; }
                if (!this.$refs.editForm.validate()) { return; }
                this.isSaving = true;
                var item = Object.assign({}, this.item);
                this.removeEmptyStringOrObjectsFromArray(item);
                try {
                    var isEdit = this.isEdit;
                    var response = await (isEdit ?
                        axios.put('/api/' + this.apiMethod + '/', item) :
                        axios.post('/api/' + this.apiMethod + '/', item));

                    this.item = response.data;
                    if (!persistForm) {
                        this.$emit('input', this.item);
                        this.$emit(isEdit ? 'update' : 'add', this.item);
                    }
                    else {
                        this.hideCancel = true;
                    }
                } catch (error) {
                    this.$emit('open');
                    errorUtility.handleServerError(error);
                }
                finally {
                    this.isSaving = false;
                }
            },

            cancel() {
                this.$emit('close');
                this.$emit('cancel');
            },

            isFiltered(property) {
                if (typeof this.filter === 'undefined' || this.filter === null) {
                    return false;
                }

                return this.filter.hasOwnProperty(property);
            },

            setItem(value, key, index) {
                if (typeof index === "undefined" || index === null) {
                    this.$set(this.item, key, value);
                }
                else {
                    if (typeof this.item[key] === "undefined" || this.item[key] === null) {
                        this.item[key] = [];
                    }
                    this.$set(this.item[key], index, value);
                }
            },

            adjustItem(key, index, value) {
                if (typeof this.item[key] === "undefined" || this.item[key] === null) {
                    this.item[key] = [];
                }
                if (typeof value === "undefined") {
                    this.item[key].splice(index, 1);
                }
                else {
                    this.item[key].splice(index, 0, value);
                }
            }
        },

        watch: {
            value: {
                immediate: true,
                handler(value) {
                    if (value) {
                        this.item = JSON.parse(JSON.stringify(value));
                    }
                    else {
                        this.item = {};

                        for (var i = 0; i < this.fields.length; i++) {
                            if (this.fields[i].type !== "custom") {
                                this.$set(this.item, this.fields[i].value, this.fields[i].type === 'checkbox' ? false : null);
                            }
                        }
                    }

                    if (typeof this.filter !== 'undefined' && this.filter !== null) {
                        Object.assign(this.item, this.filter);
                    }
                },
            },
        },

        computed: {
            isEdit() {
                return this.item !== null && typeof (this.item) !== "undefined" && this.item.id != null;
            },
            mode() {
                return this.isHover ? '' : this.isEdit ? 'Edit ' : 'Add ';
            },
            canSave() {
                if (this.item === null || typeof (this.item) === "undefined") {
                    return false;
                }
                return this.fields.filter(f => f.isRequired).every(f => this.item[f.value]);
            },
        }
    };
</script>
<style>
    .checkbox-dense {
        margin: -12px 0px 0px -5px
    }
</style>
